import React, { useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';

import StepperCard from '@/components/common/StepperCard.tsx';
import { NavigationBar } from '@/components/NavigationBar';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import { useSessionSave } from '@/hooks/use-session-save';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import { trackInputPayorsAndZip } from '@/lib/analytics';
import { setCarrierTypes } from '@/types/insurance-builder-legacy.ts';
import { isEligibleZipCode } from '@/types/insurance-builder.type.ts';
import { prettyTypeName } from '@/types/insurance.type.ts';
import {
  type PreDiscoveryData,
  PreDiscoveryForm,
} from '@/views/insurance/flows/legacy/components/PreDiscoveryForm.tsx';

export function InsurancePreDiscovery() {
  const navigate = useNavigate();
  const { saveSession } = useSessionSave();

  const { patientRecipient, patientFirstName, insuranceBuilder } = useSessionStore();

  const saveAndProgress = useCallback(
    (data: PreDiscoveryData) => {
      const { updateState, contactEmail } = useSessionStore.getState();
      const updatedBuilder = setCarrierTypes(insuranceBuilder, data.carrierTypes);

      updateState({
        insuranceZipCode: data.zipCode,
        insuranceBuilder: {
          ...updatedBuilder,
          preDiscoveryCarrierTypes: updatedBuilder.carrierTypes,
          gatheredTypes: true,
          hasPrivateInsurance: data.hasPrivateInsurance,
        },
      });

      const selectedNone = data.carrierTypes.size === 0;
      const selectedOnlyCommercial = data.hasPrivateInsurance && selectedNone;

      trackInputPayorsAndZip({
        email: contactEmail,
        insurance_types: [...data.carrierTypes]
          .map((type) => prettyTypeName(type))
          .concat(data.hasPrivateInsurance ? ['Commercial'] : []),
        insurance_zipcode: data.zipCode,
      });

      // Auto-save session (non-blocking)
      saveSession();

      if (!isEligibleZipCode(data.zipCode) || selectedOnlyCommercial || selectedNone) {
        navigate({ to: '/insurance/ineligible' });
      } else {
        navigate({ to: '/insurance/discovery-pending' });
      }
    },
    [insuranceBuilder, navigate, saveSession]
  );

  const toPatientInfo = () => navigate({ to: '/1' });

  const headerText =
    patientRecipient === 'me'
      ? 'What type of insurance plan(s) do you have?'
      : `What type of insurance plan(s) does ${patientFirstName} have?`;

  return (
    <section>
      <NavigationBar onBack={toPatientInfo} backLabel="Patient info" stepIndicator="2 of 4" />
      <StepperCard currentStep={2} totalSteps={4}>
        <CardHeader>
          <CardTitle>{headerText}</CardTitle>
        </CardHeader>
        <CardContent>
          <PreDiscoveryForm onSubmit={saveAndProgress} />
        </CardContent>
      </StepperCard>
    </section>
  );
}
